import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Sparkles, CheckCircle, ArrowRight, Heart, Users, Activity, User, Baby, Smartphone, Share, MoreVertical, PlusSquare } from 'lucide-react';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';

const ProfileSelectionSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateProfile } = useAuth();
  const [userType, setUserType] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [activeTab, setActiveTab] = useState('android');

  const userTypeConfig = {
    child: {
      label: 'Caregiver',
      icon: Baby,
      color: 'text-pink-500',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
      message: "You\'re taking an important step in caring for your loved one\'s health!"
    },
    elderly: {
      label: 'Elderly Person',
      icon: Users,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      message: "You're making a difference in supporting senior health and wellbeing!"
    },
    chronic: {
      label: 'Someone with Long Term Condition',
      icon: Activity,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      message: "You\'re taking control of your health journey with proactive tracking!"
    },
    general: {
      label: 'General Adult',
      icon: User,
      color: 'text-teal-500',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      message: "You\'re prioritizing your health with smart symptom tracking!"
    }
  };

  useEffect(() => {
    const selectedType = location?.state?.userType || localStorage.getItem('userType') || 'general';
    setUserType(selectedType);

    const saveUserType = async () => {
      try {
        await updateProfile({ 
          user_type: selectedType,
          onboarding_completed: true 
        });
      } catch (error) {
        console.error('Failed to save user type to database:', error);
      }
    };
    saveUserType();

    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, [location, updateProfile]);

  const handleStartCheckIn = () => {
    navigate('/daily-check-in');
  };

  const config = userTypeConfig?.[userType] || userTypeConfig?.general;
  const IconComponent = config?.icon;

  return (
    <>
      <Helmet>
        <title>Welcome to Your Health Journey - MyBodyToday</title>
        <meta name="description" content="Congratulations on setting up your profile! Start your first daily check-in." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-purple-50 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {showConfetti && (
            <>
              {[...Array(20)]?.map((_, i) => (
                <div
                  key={i}
                  className="absolute animate-float"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `-${Math.random() * 20}%`,
                    animationDelay: `${Math.random() * 2}s`,
                    animationDuration: `${3 + Math.random() * 2}s`
                  }}
                >
                  <Sparkles className="text-yellow-400 opacity-70" size={16 + Math.random() * 16} />
                </div>
              ))}
            </>
          )}
        </div>

        {/* Main Content Card */}
        <div className="w-full max-w-2xl mx-auto relative z-10">
          <div className="bg-white rounded-3xl shadow-2xl p-5 sm:p-8 md:p-12 border-2 border-teal-100">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-teal-400 rounded-full blur-xl opacity-30 animate-pulse"></div>
                <div className="relative bg-gradient-to-br from-teal-400 to-teal-600 rounded-full p-6">
                  <CheckCircle className="w-16 h-16 text-white" strokeWidth={2.5} />
                </div>
              </div>
            </div>

            {/* Congratulatory Header */}
            <div className="text-center mb-5 sm:mb-8">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center justify-center gap-2 sm:gap-3">
                <Sparkles className="text-yellow-500 animate-pulse" size={24} />
                Congratulations!
                <Sparkles className="text-yellow-500 animate-pulse" size={24} />
              </h1>
              <p className="text-lg sm:text-xl text-gray-600">
                You've successfully set up your profile
              </p>
            </div>

            {/* Profile Choice Display */}
            <div className={`${config?.bgColor} ${config?.borderColor} border-2 rounded-2xl p-4 sm:p-6 mb-5 sm:mb-8`}>
              <div className="flex items-center gap-4 mb-4">
                <div className={`${config?.bgColor} p-4 rounded-xl border ${config?.borderColor}`}>
                  <IconComponent className={`${config?.color} w-8 h-8`} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 mb-1">Your Profile Type</p>
                  <h2 className="text-2xl font-bold text-gray-900">{config?.label}</h2>
                </div>
                <Heart className="text-red-500 w-8 h-8 animate-pulse" fill="currentColor" />
              </div>
              <p className={`${config?.color} font-medium text-center text-lg`}>
                {config?.message}
              </p>
            </div>

            {/* Add to Home Screen Guide */}
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-4 sm:p-6 mb-5 sm:mb-8 border border-teal-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-teal-600 rounded-xl p-2">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Add MyBodyToday to Your Home Screen</h3>
                  <p className="text-sm text-gray-500">Quick access every time you need it</p>
                </div>
              </div>

              {/* Tab Switcher */}
              <div className="flex rounded-xl overflow-hidden border border-teal-200 mb-5">
                <button
                  onClick={() => setActiveTab('android')}
                  className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                    activeTab === 'android' ?'bg-teal-600 text-white' :'bg-white text-gray-600 hover:bg-teal-50'
                  }`}
                >
                  🤖 Android
                </button>
                <button
                  onClick={() => setActiveTab('iphone')}
                  className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                    activeTab === 'iphone' ?'bg-teal-600 text-white' :'bg-white text-gray-600 hover:bg-teal-50'
                  }`}
                >
                  🍎 iPhone
                </button>
              </div>

              {/* Android Instructions */}
              {activeTab === 'android' && (
                <ol className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full text-xs font-bold flex items-center justify-center mt-0.5">1</span>
                    <p className="text-sm text-gray-700">Open <strong>Chrome</strong> and visit the MyBodyToday app</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full text-xs font-bold flex items-center justify-center mt-0.5">2</span>
                    <p className="text-sm text-gray-700">Tap the <strong>three-dot menu</strong> <MoreVertical className="inline w-3.5 h-3.5 text-gray-500" /> in the top-right corner</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full text-xs font-bold flex items-center justify-center mt-0.5">3</span>
                    <p className="text-sm text-gray-700">Select <strong>"Add to Home screen"</strong></p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full text-xs font-bold flex items-center justify-center mt-0.5">4</span>
                    <p className="text-sm text-gray-700">Tap <strong>"Add"</strong> to confirm — the app icon will appear on your home screen!</p>
                  </li>
                </ol>
              )}

              {/* iPhone Instructions */}
              {activeTab === 'iphone' && (
                <ol className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full text-xs font-bold flex items-center justify-center mt-0.5">1</span>
                    <p className="text-sm text-gray-700">Open <strong>Safari</strong> and visit the MyBodyToday app</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full text-xs font-bold flex items-center justify-center mt-0.5">2</span>
                    <p className="text-sm text-gray-700">Tap the <strong>Share button</strong> <Share className="inline w-3.5 h-3.5 text-gray-500" /> at the bottom of the screen</p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full text-xs font-bold flex items-center justify-center mt-0.5">3</span>
                    <p className="text-sm text-gray-700">Scroll down and tap <strong>"Add to Home Screen"</strong> <PlusSquare className="inline w-3.5 h-3.5 text-gray-500" /></p>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-teal-600 text-white rounded-full text-xs font-bold flex items-center justify-center mt-0.5">4</span>
                    <p className="text-sm text-gray-700">Tap <strong>"Add"</strong> in the top-right corner — done! 🎉</p>
                  </li>
                </ol>
              )}

              <p className="text-xs text-gray-500 mt-4 text-center">
                You can always do this later from your browser's menu
              </p>
            </div>

            {/* Encouraging Message */}
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-4 sm:p-6 mb-5 sm:mb-8 border border-teal-100">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Sparkles className="text-teal-600" size={20} />
                Ready to Begin Your Health Journey?
              </h3>
              <p className="text-gray-700 leading-relaxed mb-2">
                Your first daily check-in is an important milestone! It takes just a few minutes and helps establish your health baseline.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Track how you're feeling, log any symptoms, and start building valuable insights about your health patterns.
              </p>
            </div>

            {/* Call to Action Button */}
            <div className="space-y-4">
              <Button
                onClick={handleStartCheckIn}
                className="w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-bold text-lg py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3"
              >
                Start My First Daily Check-In
                <ArrowRight className="w-6 h-6" />
              </Button>
              <p className="text-center text-sm text-gray-500">
                This will only take 2-3 minutes
              </p>
            </div>

            {/* Motivational Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-gray-600 italic">
                "Every health journey begins with a single step. You've just taken yours!"
              </p>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes float {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
        }
        .animate-float { animation: float linear infinite; }
      `}</style>
    </>
  );
};

export default ProfileSelectionSuccess;